import type { LDContext } from '@launchdarkly/js-client-sdk-common';
import { updateId } from 'expo-updates';
import { isEqual, merge, omit, omitBy } from 'lodash-es';
import { Platform } from 'react-native';

import { appVersion, binaryRuntimeAppVersion } from 'utils/app-version-info';
import { getDeviceId, getDeviceIdAsync } from 'utils/device-id';
import { getCurrentCommitHash, platform, region, sanityDataset } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { getLanguage } from 'utils/intl/language';
import { logger } from 'utils/logger';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace';

import { configurePlatformClient, launchDarklyIdentify } from './platform-client';
import { LDContextUpdates } from './types';

/** exposed for private LD use, you shouldn't really be reading this and its not in react either for the same reason */
export const staticLDContextUserAttrs: { current: LDContext } = {
  current: { key: 'anonymous', anonymous: true, kind: 'user' },
};

/** cross platform LD initialization */
export const initLaunchDarklyPromise = initLaunchDarkly().catch(error => {
  logger.error({ error, message: 'Error initializing LaunchDarkly' });
});

async function initLaunchDarkly() {
  const mmkvAttributes: LDContext | null = syncStorage.getObject(
    SyncStorageKey.LAUNCH_DARKLY_USER_ATTRIBUTES
  );

  // can be async on ios first time app is ever started
  await getDeviceIdAsync();

  const attributes = mmkvAttributes;

  staticLDContextUserAttrs.current = merge(
    {},
    createBaseUserAttributes(),
    omitSomeCachedAttributes(attributes)
  ) as LDContext;

  const ldInitTrace = startTrace(TraceName.APP_START_INIT_LAUNCHDARKLY);

  // delegate to native/web files to configure their core client.
  await configurePlatformClient(staticLDContextUserAttrs.current);

  ldInitTrace?.stop();
}

/**
 * Persist context and trigger identify in the background so callers never block on the network.
 * Returns immediately; errors are logged and we retry once so LD still gets updates without freezing the UI.
 */
async function updateUserWithAttributes(
  newLDContext: LDContext,
  updateReason?: string
): Promise<void> {
  // Save attributes immediately - this is cheap local storage work
  // and should not be deferred alongside the LD identify call
  saveAttributes(newLDContext);

  try {
    // waitForNetworkResults=false: we don't need to block on the network
    // response for runtime attribute updates. The app will use cached/current
    // flag values and update once the new stream is established.
    await launchDarklyIdentify(newLDContext, false);
  } catch (error) {
    logger.error({
      error,
      message: `Error updating user with LaunchDarkly attributes ${
        updateReason ? `for ${updateReason}` : ''
      }`,
    });
  }
}

function saveAttributes(newLDContext: LDContext) {
  // we don't want to persist values that are on the baseLDContext, only unique attributes of the user. it has caused bugs (like app version can change every boot!)
  const diffToStore = getDiffFromBaseLDContext(createBaseUserAttributes(), newLDContext);
  syncStorage.setObject(SyncStorageKey.LAUNCH_DARKLY_USER_ATTRIBUTES, diffToStore);
  staticLDContextUserAttrs.current = newLDContext;
}

/** conditionally update a user if something changed
 * this may cause flags to change on the provider
 */
export const updateLaunchDarklyUser = async (changes: LDContextUpdates, updateReason?: string) => {
  const normalized = normalizeUserAttributes(changes);
  const mergedAttributes = merge({}, staticLDContextUserAttrs.current, normalized) as LDContext;

  // Only update LD user if attributes are different via a deep comparison check
  if (!isEqual(staticLDContextUserAttrs.current, mergedAttributes)) {
    return updateUserWithAttributes(mergedAttributes, updateReason);
  }
  return Promise.resolve();
};

export const logoutLaunchDarklyUser = async () => {
  const baseContext = createBaseUserAttributes();
  return updateUserWithAttributes(baseContext);
};

export function getDiffFromBaseLDContext(baseLDContext: LDContext, currentUser: LDContext) {
  const base = baseLDContext as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(currentUser).filter(([key, value]) => value !== base[key])
  );
}

/**
 * we do not want to pull values from LocalStorage if they were set previously
 * omit some attributes that should never come from cache as they need to be fresh
 * NOTE they shouldn't be set in localstorage anymore but this is just in case
 * */
export function omitSomeCachedAttributes(
  cachedUserAttributes?: LDContext | null
): Partial<LDContext> {
  if (!cachedUserAttributes) {
    return {};
  }

  return omitBy(
    omit(cachedUserAttributes, [
      // these cannot come from the cache because they can change every boot
      // and break the force update modal!
      'appFlowBuildId',
      'appShellVersion',
      'expoOtaUpdateId',
      'appVersion',
      '_meta', // internal to older SDKs and should never be read or cached
    ]),
    // also remove all custom.* attributes as they are legacy, or _private fields
    (val, key) => key.startsWith('custom') || key.startsWith('_')
  );
}

function normalizeUserAttributes(userAttributeUpdates: LDContextUpdates | LDContext): LDContext {
  const trimStringValues = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (Array.isArray(value)) {
      return value.map(trimStringValues);
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, nestedValue]) => {
        acc[key] = trimStringValues(nestedValue);
        return acc;
      }, {});
    }

    return value;
  };

  const trimmedAttributes = trimStringValues(userAttributeUpdates) as LDContextUpdates;
  const { ...attributes } = trimmedAttributes;
  const email: string = trimmedAttributes.email ?? '';

  return {
    ...attributes,
    ...(email && { email: email.toLowerCase() }),
  } as LDContext;
}

function createBaseUserAttributes(): LDContext {
  const appShellVersion = binaryRuntimeAppVersion;
  const isWeb = Platform.OS === 'web';
  const anon: LDContext = {
    kind: 'user',
    key: getDeviceId() ?? '',
    anonymous: true,
    expoOtaUpdateId: updateId || '',
    appFlowBuildId: updateId || '',
    appShellVersion,
    appVersion: (isWeb ? getCurrentCommitHash() : appVersion) || '',
    country: region,
    device_id: getDeviceId(),
    host: isWeb ? window.location.host : 'NATIVE_APP',
    language: getLanguage(),
    mobileOS: Platform.OS.toLowerCase(),
    platform: platform(),
    sanityDataset: `${sanityDataset()}_${region.toLowerCase()}`,
    // this used to be hardcoded to IOS for mobile for some reason but now does ANDROID too
    userClient: isWeb ? window.navigator.userAgent || '' : Platform.OS.toUpperCase(),
  };
  return normalizeUserAttributes(anon);
}
